/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// actions
import { addOnsSettingsChanged, discardAddOnsSettings, updateAddOnsSetting } from 'state/actions/ratingsState/addOnsStatus';
// material
import { CopySnippetField, Switch, InlineMessage } from '@insticator/insticator-ui'; // iui-material
import AddOnController from 'material/expandablesection/controllers/addoncontroller/addoncontroller';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// utils
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/ratings/ratingsmanager/addons/ratingscountersection/ratingscountersection_styles';


const RatingsCounterSection = (props) => {

  const [ isOpened, setIsOpened ] = useState(false);

  const siteUUID = useSelector(state => state.ratingsState.configStatus.siteUUID);
  const code = useSelector(state => state.ratingsState.addOnsStatus.code);
  const counterConfiguration = useSelector(state => state.ratingsState.addOnsStatus.addOnsData.counterConfiguration);
  const counterConfigUUID = useSelector(state => state.ratingsState.configStatus.configuration.counterConfiguration.uuid);
  const enablePageId = useSelector(state => state.ratingsState.configStatus.configuration.enablePageId);
  const isAddOnsSettingsChanged = useSelector(state => state.ratingsState.addOnsStatus.isAddOnsSettingsChanged);

  const dispatch = useDispatch();

  useEffect(() => {
    if(siteUUID) {
      setIsOpened(false);
    }
  }, [siteUUID]);

  const toggleSwitch = () => {
    const counterConfigRequest = {
      path: 'counter-configurations',
      type: 'counterConfiguration',
      configUUID: counterConfigUUID,
      request: {
        ...counterConfiguration,
        enabled: !counterConfiguration.enabled,
        counterConfigUUID
      }
    };
    delete counterConfigRequest.request.uuid;
    dispatch(updateAddOnsSetting(counterConfigRequest));

    if(counterConfiguration.enabled)
      setIsOpened(!counterConfiguration.enabled);

    dispatch(
      addOnsSettingsChanged({
        addOnsType: 'counterConfiguration',
        property: 'enabled',
        value: !counterConfiguration.enabled
      })
    );
  }

  const toggleSetting = () => {
    if(counterConfiguration.enabled)
      setIsOpened(!isOpened);
  }

  const handleCancel = () => {
    dispatch(
      discardAddOnsSettings({
        addOnsType: 'counterConfiguration',
        ignoreProperty: {
          name: 'enabled',
          value: counterConfiguration.enabled
        }
      })
    );
    setIsOpened(false);
  }

  const handleSave = () => {
    const counterConfigRequest = {
      path: 'counter-configurations',
      type: 'counterConfiguration',
      configUUID: counterConfigUUID,
      request: {
        ...counterConfiguration,
        counterConfigUUID
      },
      showSuccessMessage: true
    };

    dispatch(updateAddOnsSetting(counterConfigRequest));
    setIsOpened(false);
  }

  const controller = () => (
    <AddOnController
      title={'Ratings Counter'}
      image={'illustrations/illustration-ratings-counter.svg'}
      description={`The Ratings Counter allows users to immediately see the average rating for a particular item. 
      The Ratings Counter can be placed anywhere on the page with a fully customizable design.`}
      isOpened={isOpened}
      setIsOpened={toggleSetting}
      toggle={counterConfiguration.enabled}
      toggleClick={toggleSwitch}
    />
  );

  return (
    <ExpandableSection
      className={`ratingscounter ${props.classes.ratingscounter}`}
      controller={controller()}
      isOpened={isOpened}
      setIsOpened={toggleSetting}
      handleCancel={handleCancel}
      handleSave={handleSave}
      disableSave={!isAddOnsSettingsChanged}
    >
      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription title='' />
            <CopySnippetField
              title={'Header Code'}
              description={<div>Place the header code inside of the <strong>&lt;head&gt;</strong> tag in your HTML source code.</div>}
              codeSnippet={code.headerCode}
              // link={{
              //   text: 'Setup instructions',
              //   url: 'https://insticator.com'
              // }}
            />
            <InlineMessage
              key={'meow'}
              display={true}
              name={'headercode'}
              variant={'neutral'}
              icon={'exclamation-triangle'}
              title={null}
              message={<span><strong>Insticator Header Code</strong> is the same for all ‘hard-coded’
              Insticator units, <strong>only add it to your site once.</strong></span>}
              close={false}
              handleClose={null}
            />
        </SectionGroup>

        <SectionGroup>
          <SectionGroupDescription title='' />
            <CopySnippetField
              title={'Ratings Counter Code'}
              description={<div>Place the Ratings Counter code inside of the <strong>&lt;body&gt;</strong> tag
              in your HTML source code where you want the Ratings Counter to appear in your site.</div>}
              codeSnippet={code.ratingCounterCode}
              // link={{
              //   text: 'Setup instructions',
              //   url: 'https://insticator.com'
              // }}
            />
            <InlineMessage
              key={'meow'}
              display={true}
              name={'ratingCountercode'}
              variant={'neutral'}
              icon={'exclamation-triangle'}
              title={null}
              message={
                <span>Make sure you replace <strong>&#123;articleURL&#125;</strong> with the link to your specific page
                {!enablePageId ? '.' :
                <span>, and <strong>&#123;extPageId&#125;</strong> with the proper External Page
                ID value for the specific page.</span>
                }
                </span>
              }
              close={false}
              handleClose={null}
            />
        </SectionGroup>
      </SectionCard>

      <SectionCard>
        <SectionGroup row>
          <SectionGroupDescription
            title='Show Only Full Stars For Ratings'
            description={`Shows only fulls stars in the Ratings Counter. 
            If turned off, half-stars will be shown in the Ratings Counter when the score includes decimals.`}
          />
          <Switch
            on={!counterConfiguration.showHalfStars}
            handleClick={() =>
              dispatch(
                addOnsSettingsChanged({
                  addOnsType: 'counterConfiguration',
                  property: 'showHalfStars',
                  value: !counterConfiguration.showHalfStars
                })
              )
            }
            variant='secondary'
          />
        </SectionGroup>
      </SectionCard>
    </ExpandableSection>
  );
}

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(RatingsCounterSection)
